import { useState, type FormEvent } from "react";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { CodeInput } from "@/components/auth/CodeInput";
import { GradientScene, SceneError } from "@/components/auth/GradientScene";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api-client";

/** The second factor, for an account that already has an authenticator enrolled. */
export function TwoFactor() {
  const { totpVerify, recoveryVerify } = useAuth();
  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submitTotp(submitted: string) {
    if (submitted.length !== 6) return;
    setError("");
    setBusy(true);
    try {
      await totpVerify(submitted, rememberDevice);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify the code. Try again.");
      setCode("");
      setBusy(false);
    }
  }

  async function submitRecovery(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const remaining = await recoveryVerify(recoveryCode.trim(), rememberDevice);
      // Recovery codes are single-use and finite. Saying so at the moment one is spent is
      // the only point at which the warning is actually useful.
      if (remaining === 0) {
        toast.warning("That was your last recovery code. Generate new ones from your profile.");
      } else if (remaining <= 3) {
        toast.warning(`${remaining} recovery ${remaining === 1 ? "code" : "codes"} left.`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify that code. Try again.");
      setBusy(false);
    }
  }

  return (
    <GradientScene>
      <div>
        <h2 className="text-xl font-bold text-white">
          {mode === "totp" ? "Two-factor authentication" : "Use a recovery code"}
        </h2>
        <p className="mt-1 text-sm text-white/60">
          {mode === "totp"
            ? "Enter the six-digit code from your authenticator app."
            : "Enter one of the codes you saved when you set up two-factor authentication."}
        </p>
      </div>

      <SceneError message={error} />

      {mode === "totp" ? (
        <div className="space-y-4">
          <CodeInput value={code} onChange={setCode} onComplete={submitTotp} disabled={busy} autoFocus />

          <label className="flex items-center justify-center gap-2.5 text-sm text-white/70">
            <input
              type="checkbox"
              className="tick"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Trust this device for 30 days
          </label>

          <button
            type="button"
            disabled={busy || code.length !== 6}
            onClick={() => submitTotp(code)}
            className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Verifying…" : "Verify"}
          </button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={submitRecovery}>
          <input
            autoFocus
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
            placeholder="ABCD2345"
            aria-label="Recovery code"
            className="glass-input mono w-full rounded-lg px-4 py-2.5 text-center text-sm tracking-widest"
          />
          <label className="flex items-center justify-center gap-2.5 text-sm text-white/70">
            <input
              type="checkbox"
              className="tick"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Trust this device for 30 days
          </label>
          <button
            type="submit"
            disabled={busy || recoveryCode.trim().length === 0}
            className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Verifying…" : "Use this code"}
          </button>
        </form>
      )}

      <p className="text-center text-xs text-white/40">
        {mode === "totp" ? "Lost your phone? " : "Got your authenticator back? "}
        <button
          type="button"
          onClick={() => {
            setMode(mode === "totp" ? "recovery" : "totp");
            setError("");
            setCode("");
            setRecoveryCode("");
          }}
          className="text-white/60 underline underline-offset-2 hover:text-white"
        >
          {mode === "totp" ? "Use a recovery code" : "Use your authenticator"}
        </button>
      </p>
    </GradientScene>
  );
}
